/**
 * Market Resilience Scanner — "Cổ phiếu mạnh ngược dòng, sẵn sàng bứt phá"
 *
 * Strategy: Find stocks with strong relative strength during market weakness,
 * showing volatility contraction (squeeze) and ready for early breakout.
 *
 * 4 Core Pillars:
 *   1. Relative Strength (RS) - Outperformance vs market during weakness
 *   2. Volatility Squeeze - Price consolidation (nén nền) after decline
 *   3. Base Quality - Strong technical setup with volume confirmation
 *   4. Trap Filters - Avoid downtrend traps and low liquidity
 */
import React, { useState, useCallback } from 'react';
import {
  Box, Typography, Button, CircularProgress, alpha, Chip,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Snackbar, Alert, Dialog, DialogTitle, DialogContent,
  IconButton, TextField, InputAdornment, Skeleton, Card,
} from '@mui/material';
import {
  Refresh, Close, Search, Insights, TrendingUp,
  CalendarToday, Speed, Shield, ShowChart, Public,
  Warning, CheckCircle,
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { marketResilienceAPI } from '../services/api';

// ── Helpers ──────────────────────────────────────────────────────────
const fmtPct = (n, d = 2) => n != null ? `${n >= 0 ? '+' : ''}${n.toFixed(d)}%` : '—';
const fmtScore = (n) => n != null ? n.toFixed(1) : '—';
const fmtNum = (n, d = 2) => n != null ? n.toFixed(d) : '—';

// ── Inline row for detail dialogs ────────────────────────────────────
const R = ({ label, value, positive, warn, sub }) => (
  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', py: 0.45,
    borderBottom: '1px solid rgba(241,245,249,0.8)', '&:last-child': { borderBottom: 'none' } }}>
    <Typography sx={{ fontSize: '0.75rem', color: '#64748b' }}>{label}</Typography>
    <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 0.4 }}>
      <Typography sx={{ fontSize: '0.82rem', fontWeight: 700, fontFamily: 'monospace',
        color: warn ? '#ef4444' : positive ? '#10b981' : '#1e293b' }}>{value}</Typography>
      {sub && <Typography sx={{ fontSize: '0.6rem', color: '#94a3b8' }}>{sub}</Typography>}
    </Box>
  </Box>
);

// ── Section header for detail dialogs ────────────────────────────────
const SectionHead = ({ icon, label, color }) => (
  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.4,
    borderLeft: `2.5px solid ${color}`, pl: 0.8, py: 0.15 }}>
    {React.cloneElement(icon, { sx: { fontSize: 12, color } })}
    <Typography sx={{ fontSize: '0.62rem', fontWeight: 800, textTransform: 'uppercase',
      letterSpacing: 0.8, color }}>{label}</Typography>
  </Box>
);

// ── Skeletons ────────────────────────────────────────────────────────
const SummaryStripSkeleton = () => (
  <Box sx={{ display: 'flex', gap: 1.5, mb: 2, flexWrap: 'wrap' }}>
    {[0, 1, 2, 3].map(i => (
      <Box key={i} sx={{ flex: '1 1 140px', minWidth: 130, borderRadius: 1.5, border: '1px solid #f1f5f9', bgcolor: '#fff', px: 2, py: 1.5, display: 'flex', alignItems: 'center', gap: 1.2 }}>
        <Skeleton variant="rounded" width={36} height={36} sx={{ borderRadius: 1.5, flexShrink: 0 }} />
        <Box>
          <Skeleton width={60} height={10} sx={{ mb: 0.5 }} />
          <Skeleton width={50} height={22} />
        </Box>
      </Box>
    ))}
  </Box>
);

const TableRowSkeleton = () => (
  <TableRow>
    {[0,1,2,3,4,5,6,7,8].map(i => (
      <TableCell key={i} sx={{ py: 1.5 }}>
        <Skeleton width={i === 0 ? 60 : i === 1 ? 80 : 50} height={20} />
      </TableCell>
    ))}
  </TableRow>
);

// ── Main Component ───────────────────────────────────────────────────
export default function MarketResilience() {
  const { t, i18n } = useTranslation();
  const currentLang = i18n.language || 'vi'; // Default to Vietnamese
  
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [detailStock, setDetailStock] = useState(null);
  const [sortField, setSortField] = useState('total_score'); // Default sort by score
  const [sortDirection, setSortDirection] = useState('desc'); // desc = high to low

  // Helper function to get localized sector name
  const getSectorName = useCallback((sectorNameObj, sectorCode) => {
    if (sectorNameObj && typeof sectorNameObj === 'object') {
      return sectorNameObj[currentLang] || sectorNameObj['vi'] || sectorNameObj['en'] || sectorCode || '—';
    }
    return sectorCode || '—';
  }, [currentLang]);

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await marketResilienceAPI.scan();
      setData(res.data);
    } catch (err) {
      setError(err.response?.data?.detail || t('marketResilience.error.title'));
      console.error('Market Resilience scan error:', err);
    } finally {
      setLoading(false);
    }
  }, [t]);

  React.useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleRefresh = () => {
    fetchData();
  };

  // Helper function to get action display style
  const getActionDisplay = useCallback((action) => {
    const actionMap = {
      'BUY': { label: t('marketResilience.signals.buy'), color: '#10b981', weight: 700 },
      'WATCH_TIGHT': { label: t('marketResilience.signals.watchTight'), color: '#f59e0b', weight: 700 },
      'WATCH': { label: t('marketResilience.signals.watch'), color: '#3b82f6', weight: 600 },
      'AGGRESSIVE': { label: t('marketResilience.signals.dip'), color: '#8b5cf6', weight: 700 },
      'MONITOR_STRONG': { label: t('marketResilience.signals.monitorStrong'), color: '#64748b', weight: 500 },
      'MONITOR_CAUTION': { label: t('marketResilience.signals.monitorCaution'), color: '#f97316', weight: 600 },
      'MONITOR': { label: t('marketResilience.signals.monitor'), color: '#94a3b8', weight: 500 },
      'AVOID': { label: t('marketResilience.signals.avoid'), color: '#ef4444', weight: 700 },
    };
    return actionMap[action] || actionMap['MONITOR'];
  }, [t]);

  const handleSort = (field) => {
    if (sortField === field) {
      // Toggle direction if clicking same field
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      // New field, default to desc for scores, asc for text
      setSortField(field);
      setSortDirection(['ticker', 'company_name', 'sector'].includes(field) ? 'asc' : 'desc');
    }
  };

  const filteredCandidates = React.useMemo(() => {
    if (!data?.candidates) return [];
    const q = searchQuery.toLowerCase().trim();
    let filtered = q ? data.candidates.filter(c =>
      c.ticker.toLowerCase().includes(q) ||
      (c.company_name && c.company_name.toLowerCase().includes(q)) ||
      (c.sector_code && c.sector_code.toLowerCase().includes(q)) ||
      (c.sector_name && (
        (c.sector_name.vi && c.sector_name.vi.toLowerCase().includes(q)) ||
        (c.sector_name.en && c.sector_name.en.toLowerCase().includes(q))
      ))
    ) : [...data.candidates];

    // Apply sorting
    filtered.sort((a, b) => {
      let aVal, bVal;
      
      // Get action priority for signal sorting
      const getActionPriority = (action) => {
        const priority = { 'BUY': 1, 'WATCH_TIGHT': 2, 'AGGRESSIVE': 3, 'WATCH': 4, 'MONITOR_STRONG': 5, 'MONITOR': 6 };
        return priority[action] || 999;
      };

      if (sortField === 'action') {
        aVal = getActionPriority(a.action);
        bVal = getActionPriority(b.action);
      } else if (sortField === 'sector') {
        aVal = a.sector_name?.vi || a.sector_code || '';
        bVal = b.sector_name?.vi || b.sector_code || '';
      } else {
        aVal = a[sortField] ?? -Infinity;
        bVal = b[sortField] ?? -Infinity;
      }

      if (aVal < bVal) return sortDirection === 'asc' ? -1 : 1;
      if (aVal > bVal) return sortDirection === 'asc' ? 1 : -1;
      return 0;
    });

    return filtered;
  }, [data, searchQuery, sortField, sortDirection]);

  const openDetail = (stock) => {
    setDetailStock(stock);
  };

  const closeDetail = () => {
    setDetailStock(null);
  };

  // ── Render Market Status Banner (explains why no candidates) ────────
  const renderMarketStatusBanner = () => {
    if (loading || !data) return null;
    const ctx = data.market_context || {};
    const isWeak = data.market_weak === true || ctx.is_weak === true;

    // When market is healthy, scanner short-circuits with total_scanned=0
    if (!isWeak) {
      const close = ctx.close ?? data.vnindex_close;
      const ma20 = ctx.ma20 ?? data.vnindex_ma20;
      const ret5d = ctx.ret_5d ?? data.vnindex_ret_5d;
      const ma20Slope = ctx.ma20_slope;
      const condMa20Met = close != null && ma20 != null && close < ma20;
      const condRet5dMet = ret5d != null && ret5d < -0.02;
      const condSlopeMet = ma20Slope != null && ma20Slope < 0;

      const StatusChip = ({ met }) => (
        <Chip
          size="small"
          label={met ? t('marketResilience.marketStatus.passed') : t('marketResilience.marketStatus.notPassed')}
          sx={{
            height: 18,
            fontSize: '0.65rem',
            fontWeight: 700,
            bgcolor: met ? alpha('#10b981', 0.12) : alpha('#94a3b8', 0.12),
            color: met ? '#10b981' : '#94a3b8',
            border: 'none',
          }}
        />
      );

      return (
        <Card sx={{
          mb: 2,
          p: { xs: 1.5, sm: 2 },
          border: '1px solid',
          borderColor: alpha('#3b82f6', 0.25),
          bgcolor: alpha('#3b82f6', 0.04),
          boxShadow: 'none',
        }}>
          <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.2 }}>
            <Box sx={{
              width: 36, height: 36, borderRadius: 1.5,
              bgcolor: alpha('#3b82f6', 0.12),
              display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
            }}>
              <Shield sx={{ fontSize: 20, color: '#3b82f6' }} />
            </Box>
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Typography sx={{ fontSize: '0.9rem', fontWeight: 700, color: '#1e293b', mb: 0.4 }}>
                {t('marketResilience.marketStatus.healthyTitle')}
              </Typography>
              <Typography sx={{ fontSize: '0.78rem', color: '#475569', lineHeight: 1.5, mb: 0.6 }}>
                {t('marketResilience.marketStatus.healthyDesc')}
              </Typography>
              <Typography sx={{ fontSize: '0.72rem', color: '#64748b', fontStyle: 'italic', mb: 1.2 }}>
                {t('marketResilience.marketStatus.healthyHint')}
              </Typography>

              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 1.2 }}>
                <Box sx={{ bgcolor: '#fff', border: '1px solid #e2e8f0', borderRadius: 1, px: 1.2, py: 0.6 }}>
                  <Typography sx={{ fontSize: '0.6rem', color: '#94a3b8', fontWeight: 600, textTransform: 'uppercase' }}>
                    {t('marketResilience.marketStatus.regimeLabel')}
                  </Typography>
                  <Typography sx={{ fontSize: '0.78rem', fontWeight: 700, color: '#1e293b' }}>
                    {data.market_regime || '—'}
                  </Typography>
                </Box>
                <Box sx={{ bgcolor: '#fff', border: '1px solid #e2e8f0', borderRadius: 1, px: 1.2, py: 0.6 }}>
                  <Typography sx={{ fontSize: '0.6rem', color: '#94a3b8', fontWeight: 600, textTransform: 'uppercase' }}>
                    {t('marketResilience.marketStatus.vnindexClose')} / {t('marketResilience.marketStatus.vnindexMa20')}
                  </Typography>
                  <Typography sx={{ fontSize: '0.78rem', fontWeight: 700, color: '#1e293b', fontFamily: 'monospace' }}>
                    {fmtNum(close, 2)} / {fmtNum(ma20, 2)}
                  </Typography>
                </Box>
                <Box sx={{ bgcolor: '#fff', border: '1px solid #e2e8f0', borderRadius: 1, px: 1.2, py: 0.6 }}>
                  <Typography sx={{ fontSize: '0.6rem', color: '#94a3b8', fontWeight: 600, textTransform: 'uppercase' }}>
                    {t('marketResilience.marketStatus.ret5dLabel')}
                  </Typography>
                  <Typography sx={{
                    fontSize: '0.78rem', fontWeight: 700, fontFamily: 'monospace',
                    color: ret5d != null && ret5d >= 0 ? '#10b981' : '#ef4444',
                  }}>
                    {ret5d != null ? fmtPct(ret5d * 100, 2) : '—'}
                  </Typography>
                </Box>
                <Box sx={{ bgcolor: '#fff', border: '1px solid #e2e8f0', borderRadius: 1, px: 1.2, py: 0.6 }}>
                  <Typography sx={{ fontSize: '0.6rem', color: '#94a3b8', fontWeight: 600, textTransform: 'uppercase' }}>
                    {t('marketResilience.marketStatus.ma20SlopeLabel')}
                  </Typography>
                  <Typography sx={{
                    fontSize: '0.78rem', fontWeight: 700, fontFamily: 'monospace',
                    color: ma20Slope != null && ma20Slope >= 0 ? '#10b981' : '#ef4444',
                  }}>
                    {ma20Slope != null ? fmtNum(ma20Slope, 2) : '—'}
                  </Typography>
                </Box>
              </Box>

              <Typography sx={{ fontSize: '0.68rem', color: '#64748b', fontWeight: 700, textTransform: 'uppercase', mb: 0.6, letterSpacing: 0.4 }}>
                {t('marketResilience.marketStatus.conditions')}
              </Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.4 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.8 }}>
                  <StatusChip met={condMa20Met} />
                  <Typography sx={{ fontSize: '0.74rem', color: '#475569' }}>
                    {t('marketResilience.marketStatus.condMa20')}
                  </Typography>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.8 }}>
                  <StatusChip met={condRet5dMet} />
                  <Typography sx={{ fontSize: '0.74rem', color: '#475569' }}>
                    {t('marketResilience.marketStatus.condRet5d')}
                  </Typography>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.8 }}>
                  <StatusChip met={condSlopeMet} />
                  <Typography sx={{ fontSize: '0.74rem', color: '#475569' }}>
                    {t('marketResilience.marketStatus.condSlope')}
                  </Typography>
                </Box>
              </Box>
            </Box>
          </Box>
        </Card>
      );
    }

    // Market is weak but no candidates passed
    if (data.total_passed === 0 && data.total_scanned > 0) {
      return (
        <Alert severity="info" icon={<Insights />} sx={{ mb: 2, fontSize: '0.85rem' }}>
          <Typography sx={{ fontSize: '0.85rem', fontWeight: 700, mb: 0.3 }}>
            {t('marketResilience.marketStatus.weakTitle')}
          </Typography>
          <Typography sx={{ fontSize: '0.78rem' }}>
            {t('marketResilience.marketStatus.weakEmpty', { count: data.total_scanned })}
          </Typography>
        </Alert>
      );
    }

    return null;
  };

  // ── Render Data Warning ──────────────────────────────────────────────
  const renderDataWarning = () => {
    if (!data?.data_warning) return null;
    const isMinor = data.days_behind <= 3;
    return (
      <Alert
        severity={isMinor ? "info" : "warning"}
        icon={isMinor ? <CheckCircle /> : <Warning />}
        sx={{ mb: 2, fontSize: '0.85rem' }}
      >
        {data.data_warning}
        {data.today && ` (Today: ${data.today})`}
      </Alert>
    );
  };

  // ── Render Summary Strip ─────────────────────────────────────────────
  const renderSummaryStrip = () => {
    if (loading && !data) return <SummaryStripSkeleton />;
    if (!data) return null;

    const stats = [
      { label: t('marketResilience.summary.candidates'), value: data.total_passed || 0, icon: <Insights />, color: '#3b82f6' },
      { label: t('marketResilience.summary.avgScore'), value: fmtScore(data.avg_score), icon: <Speed />, color: '#10b981' },
      { label: t('marketResilience.summary.avgShakeout'), value: fmtScore(data.avg_shakeout_resilience_score), icon: <Warning />, color: '#f97316' },
      { label: t('marketResilience.summary.marketRS'), value: fmtNum(data.market_context?.vnindex_rs_20 || 0, 1), icon: <ShowChart />, color: data.market_context?.vnindex_rs_20 > 0 ? '#10b981' : '#ef4444' },
      { label: t('marketResilience.summary.scanDate'), value: data.scan_date || '—', icon: <CalendarToday />, color: '#64748b' },
    ];

    return (
      <Box sx={{ display: 'flex', gap: 1.5, mb: 2, flexWrap: 'wrap' }}>
        {stats.map((s, i) => (
          <Box key={i} sx={{ 
            flex: { xs: '1 1 calc(50% - 8px)', sm: '1 1 140px' }, 
            minWidth: { xs: 'calc(50% - 8px)', sm: 130 },
            borderRadius: 1.5, 
            border: '1px solid #f1f5f9', 
            bgcolor: '#fff', 
            px: { xs: 1.5, sm: 2 }, 
            py: 1.5, 
            display: 'flex', 
            alignItems: 'center', 
            gap: { xs: 0.8, sm: 1.2 } 
          }}>
            <Box sx={{ 
              width: { xs: 32, sm: 36 }, 
              height: { xs: 32, sm: 36 }, 
              borderRadius: 1.5, 
              bgcolor: alpha(s.color, 0.1), 
              display: 'flex', 
              alignItems: 'center', 
              justifyContent: 'center', 
              flexShrink: 0 
            }}>
              {React.cloneElement(s.icon, { sx: { fontSize: { xs: 16, sm: 18 }, color: s.color } })}
            </Box>
            <Box sx={{ minWidth: 0, overflow: 'hidden' }}>
              <Typography sx={{ 
                fontSize: { xs: '0.65rem', sm: '0.68rem' }, 
                color: '#94a3b8', 
                fontWeight: 600, 
                textTransform: 'uppercase', 
                letterSpacing: 0.3,
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis'
              }}>{s.label}</Typography>
              <Typography sx={{ 
                fontSize: { xs: '1rem', sm: '1.1rem' }, 
                fontWeight: 700, 
                color: '#1e293b', 
                fontFamily: 'monospace',
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis'
              }}>{s.value}</Typography>
            </Box>
          </Box>
        ))}
      </Box>
    );
  };

  // ── Render Signal Legend ─────────────────────────────────────────────
  const renderSignalLegend = () => {
    const signals = [
      { key: 'BUY', label: t('marketResilience.signals.buy'), desc: t('marketResilience.signals.buyDesc'), color: '#10b981', weight: 700 },
      { key: 'WATCH_TIGHT', label: t('marketResilience.signals.watchTight'), desc: t('marketResilience.signals.watchTightDesc'), color: '#f59e0b', weight: 700 },
      { key: 'AGGRESSIVE', label: t('marketResilience.signals.dip'), desc: t('marketResilience.signals.dipDesc'), color: '#8b5cf6', weight: 700 },
      { key: 'WATCH', label: t('marketResilience.signals.watch'), desc: t('marketResilience.signals.watchDesc'), color: '#3b82f6', weight: 600 },
      { key: 'MONITOR', label: t('marketResilience.signals.monitor'), desc: t('marketResilience.signals.monitorDesc'), color: '#94a3b8', weight: 500 },
    ];

    return (
      <Box sx={{ mb: 2, p: { xs: 1, sm: 1.5 }, borderRadius: 1.5, border: '1px solid #f1f5f9', bgcolor: '#fafbfc' }}>
        <Box sx={{ display: 'flex', gap: { xs: 1, sm: 2 }, flexWrap: 'wrap', alignItems: 'center' }}>
          <Typography sx={{ fontSize: { xs: '0.65rem', sm: '0.7rem' }, fontWeight: 700, color: '#64748b', textTransform: 'uppercase', width: { xs: '100%', sm: 'auto' } }}>{t('marketResilience.legend.title')}:</Typography>
          {signals.map((s) => (
            <Box key={s.key} sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <Typography sx={{ fontSize: { xs: '0.7rem', sm: '0.75rem' }, fontWeight: s.weight, color: s.color, minWidth: { xs: 40, sm: 50 } }}>
                {s.label}
              </Typography>
              <Typography sx={{ fontSize: { xs: '0.65rem', sm: '0.7rem' }, color: '#64748b' }}>= {s.desc}</Typography>
            </Box>
          ))}
        </Box>
      </Box>
    );
  };

  // ── Render Table ─────────────────────────────────────────────────────
  const renderTable = () => {
    if (loading && !data) {
      return (
        <TableContainer sx={{ border: '1px solid #f1f5f9', borderRadius: 1.5, bgcolor: '#fff' }}>
          <Table size="small">
            <TableHead>
              <TableRow>
                {['Ticker', 'Name', 'Sector', 'Score', 'RS', 'Shakeout', 'Squeeze', 'Price', 'Actions'].map((h, i) => (
                  <TableCell key={i} sx={{ fontWeight: 700, fontSize: '0.7rem', textTransform: 'uppercase', color: '#64748b', bgcolor: '#f8fafc', py: 1.2 }}>{h}</TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {[0,1,2,3,4].map(i => <TableRowSkeleton key={i} />)}
            </TableBody>
          </Table>
        </TableContainer>
      );
    }

    if (!filteredCandidates.length) {
      // If the market-status banner already explains the situation, skip this generic block
      const isWeak = data?.market_weak === true || data?.market_context?.is_weak === true;
      if (data && !isWeak) return null;

      return (
        <Box sx={{ border: '1px solid #f1f5f9', borderRadius: 1.5, bgcolor: '#fff', p: 4, textAlign: 'center' }}>
          <Typography sx={{ fontSize: '0.9rem', color: '#94a3b8' }}>
            {t('marketResilience.empty.noCandidates')}
          </Typography>
        </Box>
      );
    }

    return (
      <TableContainer sx={{ border: '1px solid #f1f5f9', borderRadius: 1.5, bgcolor: '#fff' }}>
        <Table sx={{ tableLayout: 'fixed', width: '100%' }}>
          <TableHead>
            <TableRow>
              <TableCell 
                sx={{ fontWeight: 700, fontSize: '0.7rem', textTransform: 'uppercase', color: '#64748b', bgcolor: '#f8fafc', py: 1.2, cursor: 'pointer', userSelect: 'none', width: '7%' }}
                onClick={() => handleSort('ticker')}
              >
                {t('marketResilience.table.ticker')} {sortField === 'ticker' && (sortDirection === 'asc' ? '↑' : '↓')}
              </TableCell>
              <TableCell 
                sx={{ fontWeight: 700, fontSize: '0.7rem', textTransform: 'uppercase', color: '#64748b', bgcolor: '#f8fafc', py: 1.2, cursor: 'pointer', userSelect: 'none', width: '25%' }}
                onClick={() => handleSort('company_name')}
              >
                {t('marketResilience.table.name')} {sortField === 'company_name' && (sortDirection === 'asc' ? '↑' : '↓')}
              </TableCell>
              <TableCell 
                sx={{ fontWeight: 700, fontSize: '0.7rem', textTransform: 'uppercase', color: '#64748b', bgcolor: '#f8fafc', py: 1.2, cursor: 'pointer', userSelect: 'none', width: '15%' }}
                onClick={() => handleSort('sector')}
              >
                {t('marketResilience.table.sector')} {sortField === 'sector' && (sortDirection === 'asc' ? '↑' : '↓')}
              </TableCell>
              <TableCell 
                sx={{ fontWeight: 700, fontSize: '0.7rem', textTransform: 'uppercase', color: '#64748b', bgcolor: '#f8fafc', py: 1.2, cursor: 'pointer', userSelect: 'none', width: '11%' }}
                onClick={() => handleSort('action')}
              >
                {t('marketResilience.table.signal')} {sortField === 'action' && (sortDirection === 'asc' ? '↑' : '↓')}
              </TableCell>
              <TableCell 
                align="right"
                sx={{ fontWeight: 700, fontSize: '0.7rem', textTransform: 'uppercase', color: '#64748b', bgcolor: '#f8fafc', py: 1.2, cursor: 'pointer', userSelect: 'none', width: '10%' }}
                onClick={() => handleSort('total_score')}
              >
                {t('marketResilience.table.score')} {sortField === 'total_score' && (sortDirection === 'asc' ? '↑' : '↓')}
              </TableCell>
              <TableCell 
                align="right"
                sx={{ fontWeight: 700, fontSize: '0.7rem', textTransform: 'uppercase', color: '#64748b', bgcolor: '#f8fafc', py: 1.2, cursor: 'pointer', userSelect: 'none', width: '10%' }}
                onClick={() => handleSort('rs_strength_score')}
              >
                {t('marketResilience.table.rsScore')} {sortField === 'rs_strength_score' && (sortDirection === 'asc' ? '↑' : '↓')}
              </TableCell>
              <TableCell 
                align="right"
                sx={{ fontWeight: 700, fontSize: '0.7rem', textTransform: 'uppercase', color: '#64748b', bgcolor: '#f8fafc', py: 1.2, cursor: 'pointer', userSelect: 'none', width: '10%' }}
                onClick={() => handleSort('shakeout_resilience_score')}
              >
                {t('marketResilience.table.shakeout')} {sortField === 'shakeout_resilience_score' && (sortDirection === 'asc' ? '↑' : '↓')}
              </TableCell>
              <TableCell 
                align="right"
                sx={{ fontWeight: 700, fontSize: '0.7rem', textTransform: 'uppercase', color: '#64748b', bgcolor: '#f8fafc', py: 1.2, cursor: 'pointer', userSelect: 'none', width: '10%' }}
                onClick={() => handleSort('tightness_score')}
              >
                {t('marketResilience.table.squeeze')} {sortField === 'tightness_score' && (sortDirection === 'asc' ? '↑' : '↓')}
              </TableCell>
              <TableCell 
                align="right"
                sx={{ fontWeight: 700, fontSize: '0.7rem', textTransform: 'uppercase', color: '#64748b', bgcolor: '#f8fafc', py: 1.2, cursor: 'pointer', userSelect: 'none', width: '10%' }}
                onClick={() => handleSort('close')}
              >
                {t('marketResilience.table.price')} {sortField === 'close' && (sortDirection === 'asc' ? '↑' : '↓')}
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {filteredCandidates.map((c, i) => {
              const actionDisplay = getActionDisplay(c.action);
              return (
              <TableRow 
                key={i} 
                hover 
                onClick={() => openDetail(c)}
                sx={{ 
                  '&:hover': { bgcolor: '#f8fafc' },
                  cursor: 'pointer'
                }}
              >
                <TableCell sx={{ fontWeight: 700, fontFamily: 'monospace', fontSize: '0.85rem' }}>{c.ticker}</TableCell>
                <TableCell sx={{ fontSize: '0.8rem' }}>{c.company_name || '—'}</TableCell>
                <TableCell sx={{ fontSize: '0.75rem', color: '#64748b' }}>{getSectorName(c.sector_name, c.sector_code)}</TableCell>
                <TableCell>
                  <Typography sx={{ 
                    fontSize: '0.75rem', 
                    fontWeight: actionDisplay.weight, 
                    color: actionDisplay.color,
                    textTransform: 'uppercase',
                    letterSpacing: 0.3
                  }}>
                    {actionDisplay.label}
                  </Typography>
                </TableCell>
                <TableCell align="right">
                  <Typography sx={{ 
                    fontSize: '0.8rem', 
                    fontWeight: c.total_score >= 60 ? 700 : 600,
                    color: c.total_score >= 60 ? '#10b981' : '#3b82f6',
                    fontFamily: 'monospace'
                  }}>
                    {fmtScore(c.total_score)}
                  </Typography>
                </TableCell>
                <TableCell align="right" sx={{ fontFamily: 'monospace', fontSize: '0.8rem', color: '#1e293b' }}>{fmtScore(c.rs_strength_score)}</TableCell>
              <TableCell align="right">
                <Typography sx={{
                  fontFamily: 'monospace',
                  fontSize: '0.8rem',
                  color: c.shakeout_trap_flag ? '#ef4444' : '#8b5cf6',
                  fontWeight: 700,
                }}>
                  {fmtScore(c.shakeout_resilience_score)}
                </Typography>
                {c.shakeout_trap_flag && (
                  <Typography sx={{ fontSize: '0.58rem', color: '#ef4444', fontWeight: 700, textTransform: 'uppercase' }}>
                    {t('marketResilience.table.trap')}
                  </Typography>
                )}
              </TableCell>
                <TableCell align="right" sx={{ fontFamily: 'monospace', fontSize: '0.8rem', color: '#1e293b' }}>{fmtScore(c.tightness_score)}</TableCell>
                <TableCell align="right" sx={{ fontFamily: 'monospace', fontSize: '0.8rem', fontWeight: 600 }}>{fmtNum(c.close, 0)}</TableCell>
              </TableRow>
            );
            })}
          </TableBody>
        </Table>
      </TableContainer>
    );
  };

  // ── Render Detail Dialog ─────────────────────────────────────────────
  const renderDetailDialog = () => {
    if (!detailStock) return null;
    const s = detailStock;

    return (
      <Dialog open={!!detailStock} onClose={closeDetail} maxWidth="md" fullWidth>
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', pb: 1 }}>
          <Box>
            <Typography sx={{ fontSize: '1.2rem', fontWeight: 700 }}>{s.ticker}</Typography>
            <Typography sx={{ fontSize: '0.8rem', color: '#64748b' }}>{s.company_name || s.sector_name || s.sector_code || 'Unknown'}</Typography>
          </Box>
          <IconButton onClick={closeDetail}><Close /></IconButton>
        </DialogTitle>
        <DialogContent sx={{ pt: 1 }}>
          {/* Overall Score - Redesigned */}
          <Box sx={{ mb: 2 }}>
            <Typography sx={{ fontSize: '0.7rem', fontWeight: 700, textTransform: 'uppercase', color: '#64748b', mb: 1.5, letterSpacing: 0.5 }}>
              {t('marketResilience.detail.overallScore')}
            </Typography>
            <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: 1 }}>
              {[
                { label: t('marketResilience.detail.total'), value: s.total_score, color: '#1e293b', bg: '#f1f5f9' },
                { label: t('marketResilience.detail.rsStrength'), value: s.rs_strength_score, color: '#3b82f6', bg: '#eff6ff' },
                { label: t('marketResilience.detail.shakeoutResilience'), value: s.shakeout_resilience_score, color: s.shakeout_trap_flag ? '#ef4444' : '#8b5cf6', bg: s.shakeout_trap_flag ? '#fef2f2' : '#faf5ff' },
                { label: t('marketResilience.detail.squeeze'), value: s.tightness_score, color: '#f59e0b', bg: '#fffbeb' },
                { label: t('marketResilience.detail.baseQuality'), value: s.base_quality_score, color: '#10b981', bg: '#f0fdf4' },
                { label: t('marketResilience.detail.drawdownResist'), value: s.drawdown_resistance_score, color: '#8b5cf6', bg: '#faf5ff' },
                { label: t('marketResilience.detail.triggerReady'), value: s.trigger_readiness_score, color: '#ec4899', bg: '#fdf2f8' },
              ].map((item, i) => (
                <Box 
                  key={i}
                  sx={{ 
                    bgcolor: item.bg,
                    borderRadius: 1.5,
                    p: 1.5,
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    border: `1px solid ${item.color}15`,
                    transition: 'transform 0.2s, box-shadow 0.2s',
                    '&:hover': {
                      transform: 'translateY(-2px)',
                      boxShadow: `0 4px 12px ${item.color}20`
                    }
                  }}
                >
                  <Typography sx={{ 
                    fontSize: '0.65rem', 
                    color: '#94a3b8', 
                    fontWeight: 600,
                    textAlign: 'center',
                    mb: 0.5,
                    lineHeight: 1.2,
                    textTransform: 'uppercase',
                    letterSpacing: 0.3
                  }}>
                    {item.label}
                  </Typography>
                  <Typography sx={{ 
                    fontSize: '1.8rem', 
                    fontWeight: 700, 
                    fontFamily: 'monospace', 
                    color: item.color,
                    lineHeight: 1
                  }}>
                    {fmtScore(item.value)}
                  </Typography>
                </Box>
              ))}
            </Box>
          </Box>

          {/* Relative Strength */}
          <Box sx={{ mb: 2 }}>
            <SectionHead icon={<ShowChart />} label={t('marketResilience.detail.relativeStrength')} color="#3b82f6" />
            <Box sx={{ bgcolor: '#fff', border: '1px solid #f1f5f9', borderRadius: 1, p: 1.5 }}>
              <R label={t('marketResilience.detail.excessReturn5d')} value={fmtPct(s.excess_ret_5d)} positive={s.excess_ret_5d > 0} />
              <R label={t('marketResilience.detail.excessReturn10d')} value={fmtPct(s.excess_ret_10d)} positive={s.excess_ret_10d > 0} />
              <R label={t('marketResilience.detail.rsRatio')} value={fmtNum(s.rs_ratio, 1)} positive={s.rs_ratio > 1} />
              <R label={t('marketResilience.detail.rsSlope20')} value={fmtNum(s.rs_slope_20, 3)} positive={s.rs_slope_20 > 0} />
              <R label={t('marketResilience.detail.rsRank120')} value={fmtPct(s.rs_rank_120)} positive={s.rs_rank_120 > 0.7} />
            </Box>
          </Box>

          {/* Bad-Day Resilience */}
          <Box sx={{ mb: 2 }}>
            <SectionHead icon={<Warning />} label={t('marketResilience.detail.badDayResilience')} color={s.shakeout_trap_flag ? '#ef4444' : '#8b5cf6'} />
            {s.shakeout_trap_flag && (
              <Alert severity="warning" sx={{ mb: 1, fontSize: '0.78rem' }}>
                {t('marketResilience.detail.shakeoutTrapWarning')}
              </Alert>
            )}
            <Box sx={{ bgcolor: '#fff', border: '1px solid #f1f5f9', borderRadius: 1, p: 1.5 }}>
              <R label={t('marketResilience.detail.badDayCount20')} value={fmtNum(s.bad_day_count_20, 0)} positive={s.bad_day_count_20 >= 3} />
              <R label={t('marketResilience.detail.badDayOutperformRatio')} value={fmtPct(s.bad_day_outperform_ratio * 100)} positive={s.bad_day_outperform_ratio >= 0.6} />
              <R label={t('marketResilience.detail.badDayLowReactionRatio')} value={fmtPct(s.bad_day_low_reaction_ratio * 100)} positive={s.bad_day_low_reaction_ratio >= 0.6} />
              <R label={t('marketResilience.detail.badDayClosePositionAvg')} value={fmtPct(s.bad_day_close_position_avg * 100)} positive={s.bad_day_close_position_avg >= 0.55} />
              <R label={t('marketResilience.detail.badDayNoPanicVolumeRatio')} value={fmtPct(s.bad_day_no_panic_volume_ratio * 100)} positive={s.bad_day_no_panic_volume_ratio >= 0.7} />
              <R label={t('marketResilience.detail.badDayAbsorptionRatio')} value={fmtPct(s.bad_day_absorption_ratio * 100)} positive={s.bad_day_absorption_ratio >= 0.4} />
              <R label={t('marketResilience.detail.badDayRelativeStrength')} value={fmtPct(s.bad_day_relative_strength * 100)} positive={s.bad_day_relative_strength > 0} />
            </Box>
          </Box>

          {/* Volatility Squeeze */}
          <Box sx={{ mb: 2 }}>
            <SectionHead icon={<Speed />} label={t('marketResilience.detail.volatilitySqueeze')} color="#f59e0b" />
            <Box sx={{ bgcolor: '#fff', border: '1px solid #f1f5f9', borderRadius: 1, p: 1.5 }}>
              <R label={t('marketResilience.detail.atrPct5')} value={fmtPct(s.atr_pct_5)} />
              <R label={t('marketResilience.detail.atrPct20')} value={fmtPct(s.atr_pct_20)} />
              <R label={t('marketResilience.detail.avgRange5')} value={fmtPct(s.avg_range_5)} />
              <R label={t('marketResilience.detail.avgRange20')} value={fmtPct(s.avg_range_20)} />
              <R label={t('marketResilience.detail.bbWidth')} value={fmtPct(s.bb_width)} />
              <R label={t('marketResilience.detail.bbWidthRank120')} value={fmtPct(s.bb_width_rank_120)} />
              <R label={t('marketResilience.detail.bbWidthContraction20')} value={fmtPct(s.bb_width_contraction_20)} positive={s.bb_width_contraction_20 > 0.12} />
            </Box>
          </Box>

          {/* Base Quality */}
          <Box sx={{ mb: 2 }}>
            <SectionHead icon={<Shield />} label={t('marketResilience.detail.baseQualitySection')} color="#10b981" />
            <Box sx={{ bgcolor: '#fff', border: '1px solid #f1f5f9', borderRadius: 1, p: 1.5 }}>
              <R label={t('marketResilience.detail.distanceFromMA10')} value={fmtPct(s.distance_from_ma10 - 1)} positive={(s.distance_from_ma10 - 1) > 0} />
              <R label={t('marketResilience.detail.distanceFromMA20')} value={fmtPct(s.distance_from_ma20 - 1)} positive={(s.distance_from_ma20 - 1) > 0} />
              <R label={t('marketResilience.detail.ma10ma20Ratio')} value={fmtNum(s.ma10_ma20_ratio, 3)} positive={s.ma10_ma20_ratio > 1} />
              <R label={t('marketResilience.detail.ma20Slope')} value={fmtNum(s.ma20_slope, 1)} positive={s.ma20_slope > 0} />
              <R label={t('marketResilience.detail.closePosition10')} value={fmtPct(s.close_position_10)} positive={s.close_position_10 > 0.5} />
              <R label={t('marketResilience.detail.downVolRatio10')} value={fmtPct(s.down_volume_ratio_10)} warn={s.down_volume_ratio_10 > 0.5} />
              <R label={t('marketResilience.detail.upDayCount5')} value={fmtNum(s.up_day_count_5, 0)} positive={s.up_day_count_5 >= 3} />
              <R label={t('marketResilience.detail.reversalTailCount10')} value={fmtNum(s.reversal_tail_count_10, 0)} positive={s.reversal_tail_count_10 >= 5} />
            </Box>
          </Box>

          {/* Performance */}
          <Box sx={{ mb: 2 }}>
            <SectionHead icon={<TrendingUp />} label={t('marketResilience.detail.performance')} color="#06b6d4" />
            <Box sx={{ bgcolor: '#fff', border: '1px solid #f1f5f9', borderRadius: 1, p: 1.5 }}>
              <R label={t('marketResilience.detail.return5d')} value={fmtPct(s.ret_5d)} positive={s.ret_5d > 0} />
              <R label={t('marketResilience.detail.return10d')} value={fmtPct(s.ret_10d)} positive={s.ret_10d > 0} />
              <R label={t('marketResilience.detail.indexReturn5d')} value={fmtPct(s.index_ret_5d)} positive={s.index_ret_5d > 0} />
              <R label={t('marketResilience.detail.indexReturn10d')} value={fmtPct(s.index_ret_10d)} positive={s.index_ret_10d > 0} />
              <R label={t('marketResilience.detail.excessReturn5d')} value={fmtPct(s.excess_ret_5d)} positive={s.excess_ret_5d > 0} />
              <R label={t('marketResilience.detail.excessReturn10d')} value={fmtPct(s.excess_ret_10d)} positive={s.excess_ret_10d > 0} />
            </Box>
          </Box>

          {/* Market Info */}
          <Box sx={{ mb: 2 }}>
            <SectionHead icon={<Public />} label={t('marketResilience.detail.marketInfo')} color="#64748b" />
            <Box sx={{ bgcolor: '#fff', border: '1px solid #f1f5f9', borderRadius: 1, p: 1.5 }}>
              <R label={t('marketResilience.detail.price')} value={fmtNum(s.close, 0)} />
              <R label={t('marketResilience.detail.volume')} value={fmtNum(s.volume, 0)} />
              <R label={t('marketResilience.detail.avgValue20')} value={fmtNum(s.avg_value_20 / 1e9, 1)} />
              <R label={t('marketResilience.detail.ma10')} value={fmtNum(s.ma10, 0)} />
              <R label={t('marketResilience.detail.ma20')} value={fmtNum(s.ma20, 0)} />
              <R label={t('marketResilience.detail.ma50')} value={fmtNum(s.ma50, 0)} />
              <R label={t('marketResilience.detail.sector')} value={getSectorName(s.sector_name, s.sector_code)} />
              <R label={t('marketResilience.detail.marketRegime')} value={s.market_regime || '—'} warn={s.market_regime === 'BEAR'} />
              <R label={t('marketResilience.detail.action')} value={s.action || '—'} />
              <R label={t('marketResilience.detail.entryTrigger')} value={s.entry_trigger || '—'} />
            </Box>
          </Box>
        </DialogContent>
      </Dialog>
    );
  };

  // ── Main Render ──────────────────────────────────────────────────────
  return (
    <Box>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <Box sx={{
            width: 44, 
            height: 44, 
            borderRadius: 2,
            background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
            display: 'flex', 
            alignItems: 'center', 
            justifyContent: 'center',
            boxShadow: '0 3px 10px rgba(16, 185, 129, 0.25)',
          }}>
            <Shield sx={{ color: '#fff', fontSize: 22 }} />
          </Box>
          <Box>
            <Typography sx={{ fontWeight: 700, color: '#0f172a', fontSize: '1.2rem', letterSpacing: '-0.01em' }}>
              {t('marketResilience.title')}
            </Typography>
            <Typography sx={{ color: '#94a3b8', fontSize: '0.75rem', mt: 0.15 }}>
              {t('marketResilience.subtitle')}
            </Typography>
          </Box>
        </Box>
        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
          <TextField
            size="small"
            placeholder={t('marketResilience.searchPlaceholder')}
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            slotProps={{
              input: {
                startAdornment: (
                  <InputAdornment position="start">
                    <Search fontSize="small" sx={{ color: '#94a3b8' }} />
                  </InputAdornment>
                ),
              },
            }}
            sx={{ width: 280 }}
          />
          <Button
            variant="outlined"
            startIcon={loading ? <CircularProgress size={16} /> : <Refresh />}
            onClick={handleRefresh}
            disabled={loading}
            sx={{ textTransform: 'none', width: { xs: '100%', sm: 'auto' } }}
          >
            {loading ? t('marketResilience.actions.refreshing') : t('marketResilience.actions.refresh')}
          </Button>
        </Box>
      </Box>

      {/* Data Warning */}
      {renderDataWarning()}

      {/* Market Status Banner (explains why scanner is idle / empty) */}
      {renderMarketStatusBanner()}

      {/* Summary Strip */}
      {renderSummaryStrip()}
      
      {/* Signal Legend */}
      {renderSignalLegend()}

      {/* Table */}
      {renderTable()}

      {/* Detail Dialog */}
      {renderDetailDialog()}

      {/* Error Snackbar */}
      <Snackbar open={!!error} autoHideDuration={6000} onClose={() => setError(null)}>
        <Alert severity="error" onClose={() => setError(null)}>{error}</Alert>
      </Snackbar>
    </Box>
  );
}
